import type { BlockLikert, Theme } from 'shared-types';

interface Params {
  colormapType?: BlockLikert['colormap'];
  numberOfColors: number;
  theme: Pick<
    Theme,
    | 'dangerColor'
    | 'lightGreyDarkActive'
    | 'successStandaloneColor'
    | 'textColor'
  >;
}

const getLikertOptionColors = ({
  colormapType = 'divergent',
  numberOfColors,
  theme
}: Params) => {
  let backgroundColors: number[][] = [];

  if (colormapType === 'divergent') {
    const startColor = hexToRgb(theme.dangerColor);
    const midPoint = hexToRgb(theme.lightGreyDarkActive);
    const endColor = hexToRgb(theme.successStandaloneColor);

    const halfway = numberOfColors / 2;
    const flooredHalfway = Math.floor(halfway);

    backgroundColors = Array.from({ length: numberOfColors }, (_, i) => {
      let rgb: number[] = [];

      const index = i + 1;
      if (index === 1) {
        rgb = startColor;
      } else if (index === numberOfColors) {
        rgb = endColor;
      } else if (index === halfway + 0.5) {
        rgb = midPoint;
      } else if (index <= halfway) {
        const r =
          startColor[0] +
          (Math.abs(midPoint[0] - startColor[0]) / flooredHalfway) * i;
        const g =
          startColor[1] +
          (Math.abs(midPoint[1] - startColor[1]) / flooredHalfway) * i;
        const b =
          startColor[2] +
          (Math.abs(midPoint[2] - startColor[2]) / flooredHalfway) * i;

        rgb = [colorBounds(r), colorBounds(g), colorBounds(b)];
      } else {
        // index > halfway
        const normalizedI = i - flooredHalfway;

        const r =
          endColor[0] +
          (Math.abs(endColor[0] - midPoint[0]) / flooredHalfway) * normalizedI;
        const g =
          endColor[1] +
          (Math.abs(endColor[1] - midPoint[1]) / flooredHalfway) * normalizedI;
        const b =
          endColor[2] +
          (Math.abs(endColor[2] - midPoint[2]) / flooredHalfway) * normalizedI;

        rgb = [colorBounds(r), colorBounds(g), colorBounds(b)];
      }

      return rgb;
    });

    // sort colors within the halfway steps to ensure light > dark transitions
    const isEven = numberOfColors % 2 === 0;

    const firstHalf = sortColors(
      backgroundColors.slice(0, flooredHalfway),
      'ASC'
    );

    const secondHalf = sortColors(
      backgroundColors.slice(isEven ? flooredHalfway : flooredHalfway + 1),
      'DESC'
    );

    backgroundColors = [
      ...firstHalf,
      ...(isEven ? [] : [midPoint]),
      ...secondHalf
    ];
  } else if (colormapType === 'gradient') {
    const startColor = hexToRgb(theme.lightGreyDarkActive);
    const endColor = hexToRgb(theme.successStandaloneColor);

    const stepLength = [
      Math.abs(endColor[0] - startColor[0]) / numberOfColors,
      Math.abs(endColor[1] - startColor[1]) / numberOfColors,
      Math.abs(endColor[2] - startColor[2]) / numberOfColors
    ];

    backgroundColors = Array.from({ length: numberOfColors }, (_, i) => {
      let rgb: number[] = [];

      const index = i + 1;
      if (index === 1) {
        rgb = startColor;
      } else if (index === numberOfColors) {
        rgb = endColor;
      } else {
        const r = endColor[0] + stepLength[0] * i;
        const g = endColor[1] + stepLength[1] * i;
        const b = endColor[2] + stepLength[2] * i;

        rgb = [colorBounds(r), colorBounds(g), colorBounds(b)];
      }

      return rgb;
    });

    backgroundColors = sortColors(backgroundColors, 'DESC');
  } else {
    // discrete colormap
    backgroundColors = [
      '#8d5a97',
      '#4d9de0',
      '#e15554',
      '#e1bc29',
      '#b4dc7f',
      '#baa898',
      '#f78154',
      '#c67c0a',
      '#499f68',
      '#bb0022'
    ].map((hexColor) => hexToRgb(hexColor));
  }

  return backgroundColors.reduce((allOptions, option, index) => {
    const textColor = getTextColorForBackground(option, theme);
    allOptions[index] = { backgroundColor: rgbToHex(option), color: textColor };
    return allOptions;
  }, {} as { [index: string]: Record<'backgroundColor' | 'color', string> });
};

// https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
// uses gray as fallback color, but should never be really needed
const hexToRgb = (hex: string) =>
  hex
    .replace(
      /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
      (m, r, g, b) => '#' + r + r + g + g + b + b
    )
    .substring(1)
    .match(/.{2}/g)
    ?.map((x) => parseInt(x, 16)) || [104, 104, 104];

const rgbToHex = (rgbArray: number[]) =>
  '#' +
  rgbArray
    .map((color) => {
      const hex = color.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    })
    .join('');

const getTextColorForBackground = (
  rgbArray: number[],
  theme: Pick<Theme, 'textColor'>
) => {
  // follows W3C guidelines for contrast between text color and background
  // https://www.w3.org/WAI/WCAG22/Techniques/general/G18.html

  const normalized = rgbArray.map((color) => {
    const normalizedColor = color / 255;
    return normalizedColor <= 0.03928
      ? normalizedColor / 12.92
      : Math.pow((normalizedColor + 0.055) / 1.055, 2.4);
  });

  const luminance =
    normalized[0] * 0.2126 + normalized[1] * 0.7152 + normalized[2] * 0.0722;

  return luminance > Math.sqrt(1.05 * 0.05) - 0.05
    ? theme.textColor
    : '#ffffff';
};

const colorBounds = (value: number) => {
  const roundedValue = Math.round(value);
  return roundedValue > 255 ? 255 : roundedValue < 0 ? 0 : roundedValue;
};

const sortColors = (rgbArray: number[][], direction: 'ASC' | 'DESC') => {
  return rgbArray.sort((a, b) =>
    a[0] + a[1] + a[2] >= b[0] + b[1] + b[2]
      ? direction === 'ASC'
        ? 1
        : -1
      : direction === 'ASC'
      ? -1
      : 1
  );
};

export default getLikertOptionColors;
